/**
 * Uploads — roadmap 3.2, §9.2.
 *
 * ⚠️ **One call, since 20 October 2026.**
 *
 *   `POST /api/v1/uploads/` — multipart: the file plus `kind`, and the
 *   response is a ready `MediaAsset` with a usable `url`.
 *
 * It replaced a three-step dance: sign, POST the bytes straight to S3, then
 * confirm so the server could read them back and run SEC-08's magic-byte
 * check. The product owner replaced it knowingly — three steps with a failure
 * mode at each is too much for the mobile client to carry — and the capacity
 * cost is recorded as Gap 56.
 *
 * **The magic-byte check did not move, it got earlier.** The server now holds
 * the stream and validates the leading bytes *before* writing to storage, so a
 * client cannot skip the step that catches a renamed executable: there is no
 * separate step to skip.
 *
 * **Nothing here talks to S3 any more.** No cross-origin `fetch`, so no
 * `connect-src` entry for the bucket, and the session cookie travels only to
 * our own origin — which is what `credentials` was being kept away from
 * before.
 *
 * Video is unchanged and does not come through here: its bytes go
 * browser-to-provider (§9.2).
 */
import { useMutation, type UseMutationResult } from '@tanstack/react-query'

import { api, unwrap } from './client'
import type { MediaAsset } from './types'

export type UploadInput = { file: File; kind?: 'image' | 'document' }

/** Anything the upload itself can go wrong with. */
export class UploadFailed extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'UploadFailed'
  }
}

/**
 * The caps the server enforces, by kind — `UPLOAD_MAX_IMAGE_BYTES` and
 * `UPLOAD_MAX_DOCUMENT_BYTES`.
 *
 * ⚠️ **A courtesy, not the limit.** The server rejects an oversize file on its
 * own `settings` value and the edge refuses the body at
 * `client_max_body_size`; a limit the client is merely asked to respect is not
 * a limit. Checking here means a user who picks a 50 MB photograph is told so
 * immediately instead of pushing it over a mobile connection to be refused —
 * which, now that the bytes come through Django, also saves a worker the
 * transfer. Duplicated rather than fetched because the old flow learned it
 * from the sign response and there is no sign call left to learn it from; if
 * the settings change, this changes with them.
 */
const MAX_BYTES: Record<NonNullable<UploadInput['kind']>, number> = {
  image: 10 * 1024 * 1024,
  document: 25 * 1024 * 1024,
}

async function upload({ file, kind = 'image' }: UploadInput): Promise<MediaAsset> {
  if (file.size > MAX_BYTES[kind]) {
    throw new UploadFailed('file-too-large')
  }

  const body = new FormData()
  body.append('file', file)
  body.append('kind', kind)

  // No `bodySerializer` override, and that is checked rather than assumed:
  // openapi-fetch's `defaultBodySerializer` returns a `FormData` body
  // untouched and deliberately sends no `Content-Type`, so the browser writes
  // the multipart boundary itself (openapi-fetch 0.17, `dist/index.mjs`). An
  // override would be redundant; what must not happen is the body reaching
  // `JSON.stringify`, because a `File` serialises to `{}` and the request
  // arrives at Django carrying no file at all — with nothing for the
  // magic-byte check to inspect. `upload.test.tsx` asserts the boundary is
  // there, which is the observable proof.
  //
  // The cast is the one piece of friction: the generated type for a
  // multipart body is the field object, not the `FormData` that carries it.
  return unwrap(
    api.POST('/api/v1/uploads/', {
      body: body as unknown as never,
    }),
  )
}

export function useUpload(): UseMutationResult<MediaAsset, unknown, UploadInput> {
  return useMutation({
    mutationFn: upload,
    // One attempt. A retry re-sends the whole file, which on a flaky
    // connection means several copies of the same photograph in the bucket —
    // and the user pressed the button once. It also means a second worker
    // occupied for a second full transfer (Gap 56).
    retry: false,
  })
}
